import { fromLayout } from "@empire/shared";
import { create } from "zustand";
import {
  SIM_VERSION,
  idx,
  simulate,
  validateLayout,
  type BattleResult,
  type Layout,
} from "@empire/core-sim";
import { sendTelemetry } from "../api/attack.js";
import { TUTORIAL, type TutorialStep } from "../tutorial/steps.js";
import { rerun, type Frame } from "./replay.js";
import { useEditorStore } from "./editor.js";
import { useSessionStore } from "./session.js";

/**
 * The first ten minutes (S6-1) and the survey that follows it (S6-2).
 *
 * It runs entirely on the device. No server call decides anything here, and
 * nothing waits for one: a stranger meets the game before meeting a loading
 * spinner, and the free tier going to sleep cannot break somebody's first
 * impression. Telemetry is sent if a session happens to exist by then, and
 * silently skipped if it does not — a survey answer is worth less than the
 * tutorial actually starting.
 */

/**
 * v2 because the tutorial changed shape.
 *
 * The key is also the reason the old one could only ever run once: it was set
 * on the first view OR the first Skip, and nothing in the app could clear it.
 * `restart()` now can, and the More tab offers it.
 */
const DONE_KEY = "empire.tutorial.v2";
/**
 * The survey waits for the player's first real attack (29 Sep 2026).
 *
 * It asks how attacking AND defending felt. The shortened tutorial no longer
 * has an attack in it — the first one is the guide's raid on the Barbarian
 * Outpost — so asking at the end of the tutorial would ask about something
 * they have not done. "pending" after the tutorial, "ready" after their first
 * battle, gone once answered or declined.
 */
const SURVEY_KEY = "empire.survey";
/** Where finish() leaves the T2 maze for the save store to pick up. */
export const TUTORIAL_MAZE_KEY = "empire.tutorial.maze";

export type Phase = "intro" | "playing" | "resolved" | "survey" | "done";

export interface SurveyAnswers {
  /** 1–5. The Phase 0 gate needs BOTH of these at 3.5 or better (01 §5). */
  attacking: number | null;
  defending: number | null;
  /** The gate's other half: ≥ 60% want a second battle. */
  secondBattle: boolean | null;
}

interface TutorialState {
  active: boolean;
  index: number;
  phase: Phase;
  attemptsLeft: number;
  result: BattleResult | null;
  frames: Frame[];
  /**
   * True while a resolved battle is still playing on the board.
   *
   * The coach used to announce the result ("Held. Three attackers, none reached
   * the middle.") the instant Watch was pressed — before a single unit had
   * walked on. The replay is the lesson; the verdict waits for it.
   */
  watching: boolean;
  /** What the player built, for a "build" step. */
  built: Layout | null;
  /**
   * The last maze the player built and fought with in T2. Unlike `built` it
   * survives to the end of the tutorial, because it becomes their village's
   * first defence (D-112) instead of being thrown away.
   */
  kept: Layout | null;
  /** Why the last tap did nothing. Shown by the coach, never in a tooltip. */
  nudge: string | null;
  /**
   * The board the player had before the tutorial borrowed the editor.
   *
   * The build beats draw on the real editor store, which is also where a
   * returning player's saved village sits. Without this, replaying the tutorial
   * would scribble over their actual defence — and the Save button would
   * happily send it.
   */
  saved: Layout | null;
  answers: SurveyAnswers;

  start: () => void;
  /** Forget that it was ever seen, and run it again. */
  restart: () => void;
  skip: () => void;
  /** Run the current step's scripted battle. */
  resolve: () => void;
  /** The replay reached its end, or the player skipped to it. */
  finishWatching: () => void;
  /** The one button: finish this step if it is finished, otherwise say why. */
  advance: () => void;
  /** Move on, or retry a failed build. */
  next: () => void;
  retry: () => void;
  answer: (patch: Partial<SurveyAnswers>) => void;
  submitSurvey: () => Promise<void>;
  /** Leave without answering. Counted, so the gate knows how many declined. */
  skipSurvey: () => void;
}

const storage = {
  get(key: string): string | null {
    try {
      return globalThis.localStorage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      globalThis.localStorage?.setItem(key, value);
    } catch {
      /* a tutorial that cannot be remembered is better than one that crashes */
    }
  },
  remove(key: string): void {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      /* ignore */
    }
  },
};

export const hasSeenTutorial = (): boolean => storage.get(DONE_KEY) === "done";

/** Someone logging in to a saved village has played it before (D-116). */
export const markTutorialSeen = (): void => storage.set(DONE_KEY, "done");

/** A brand-new account on a device where someone else already played it (D-117). */
export const resetTutorialSeen = (): void => storage.remove(DONE_KEY);

/** Fire and forget: telemetry must never be able to block a player. */
function track(name: string, props: Record<string, unknown> = {}): void {
  void (async () => {
    const token = await useSessionStore.getState().token();
    if (!token) return;
    await sendTelemetry(token, [{ name, props, ts: new Date().toISOString() }]).catch(
      () => undefined
    );
  })();
}

/** How many towers are actually built on this board. */
export function towersBuilt(layout: Layout): number {
  return layout.tiles.filter((t) => t.tower).length;
}

/** Is the gate tile part of the road? Until it is, there is no way in. */
function gatePlaced(layout: Layout): boolean {
  const kind = layout.tiles[idx(layout.gate.x, layout.gate.y, layout.size)]?.kind;
  return kind === "road" || kind === "mud" || kind === "barricade";
}

/**
 * Is this step's goal met, and if not, what should the coach say?
 *
 * The road beat borrows core-sim's own validation message rather than writing
 * its own: the sentence a learner reads here is then word for word the one the
 * real editor shows, so the lesson transfers instead of having to be unlearned.
 */
export function goalState(
  step: TutorialStep,
  layout: Layout
): { done: boolean; nudge: string | null } {
  switch (step.goal) {
    case "gate":
      return gatePlaced(layout)
        ? { done: true, nudge: null }
        : { done: false, nudge: step.nudge ?? null };
    case "road": {
      const check = validateLayout(layout);
      return check.ok ? { done: true, nudge: null } : { done: false, nudge: check.error.message };
    }
    case "towers": {
      const need = step.minTowers ?? 1;
      const have = towersBuilt(layout);
      if (have >= need) return { done: true, nudge: null };
      return {
        done: false,
        nudge: have === 0 ? (step.nudge ?? null) : `One more tower — ${have} of ${need} so far.`,
      };
    }
    default: {
      // "battle", and the watch and attack beats: the button resolves a battle,
      // and the only thing that can stop it is an illegal board.
      if (step.beat === "watch" || step.beat === "attack") return { done: true, nudge: null };
      const check = validateLayout(layout);
      return check.ok ? { done: true, nudge: null } : { done: false, nudge: check.error.message };
    }
  }
}

export const useTutorialStore = create<TutorialState>((set, get) => ({
  active: false,
  index: 0,
  phase: "intro",
  attemptsLeft: 0,
  result: null,
  frames: [],
  watching: false,
  built: null,
  kept: null,
  nudge: null,
  saved: null,
  answers: { attacking: null, defending: null, secondBattle: null },

  start: () => {
    track("tutorial_started");
    const editor = useEditorStore.getState();
    const saved = editor.layout;
    const first = TUTORIAL[0];
    if (first?.resets) editor.load(first.layout);
    set({
      active: true,
      index: 0,
      phase: "playing",
      attemptsLeft: first?.retries ?? 0,
      result: null,
      frames: [],
      built: null,
      nudge: null,
      saved,
    });
  },

  restart: () => {
    storage.remove(DONE_KEY);
    get().start();
  },

  skip: () => {
    // Recorded, because "how many people walk out of the tutorial" is a
    // number worth having before drawing conclusions from the survey.
    track("tutorial_skipped", { step: TUTORIAL[get().index]?.id ?? null });
    finish(get, set);
  },

  resolve: () => {
    const step = TUTORIAL[get().index];
    if (!step) return;

    // On a build step the defence is whatever the player drew, and it has to
    // pass the same rules as a real one. Anything else would teach a lesson
    // the real editor then contradicts.
    const layout = step.beat === "build" ? useEditorStore.getState().layout : step.layout;
    const state = goalState(step, layout);
    if (!state.done) {
      set({ nudge: state.nudge });
      return;
    }

    const { frames, hash } = rerun(layout, { plan: step.plan });
    const result = simulate({ simVersion: SIM_VERSION, layout, plan: step.plan });
    void hash;

    track("tutorial_step_resolved", {
      step: step.id,
      outcome: result.outcome,
      reached: result.army.reached,
      attemptsLeft: get().attemptsLeft,
    });

    set({
      phase: "resolved",
      result,
      frames,
      watching: frames.length > 0,
      built: step.beat === "build" ? layout : null,
      kept: step.beat === "build" ? layout : get().kept,
      nudge: null,
    });
  },

  finishWatching: () => {
    if (get().watching) set({ watching: false });
  },

  /**
   * The only button on a build beat that has no battle.
   *
   * Never disabled. A step the player has not finished answers with a sentence
   * instead of going quiet, because a dead button on a phone tells you nothing
   * at all — that was the old tutorial's worst moment.
   */
  advance: () => {
    const step = TUTORIAL[get().index];
    if (!step) return;
    const layout = useEditorStore.getState().layout;
    const state = goalState(step, layout);
    if (!state.done) {
      set({ nudge: state.nudge });
      return;
    }
    // Straight on to the next step. There used to be a "Good. …" screen with
    // its own Next button between every beat — a tap that taught nothing, in
    // a tutorial people found too long (29 Sep 2026).
    get().next();
  },

  next: () => {
    const index = get().index + 1;
    const step = TUTORIAL[index];

    if (!step) {
      // Straight to the village. The survey follows their first real attack.
      track("tutorial_finished");
      if (storage.get(SURVEY_KEY) === null) storage.set(SURVEY_KEY, "pending");
      finish(get, set);
      return;
    }

    if (step.resets) useEditorStore.getState().load(step.layout);

    track("tutorial_step_started", { step: step.id });
    set({
      index,
      phase: "playing",
      attemptsLeft: step.retries,
      result: null,
      frames: [],
      watching: false,
      nudge: null,
    });
  },

  retry: () => {
    const left = get().attemptsLeft;
    if (left <= 0) return;
    track("tutorial_retry", { step: TUTORIAL[get().index]?.id ?? null });
    set({ attemptsLeft: left - 1, phase: "playing", result: null, frames: [], watching: false, nudge: null });
  },

  answer: (patch) => set({ answers: { ...get().answers, ...patch } }),

  skipSurvey: () => {
    track("survey_skipped");
    storage.set(SURVEY_KEY, "done");
    finish(get, set);
  },

  submitSurvey: async () => {
    const { answers } = get();
    const token = await useSessionStore.getState().token();
    if (token) {
      await sendTelemetry(token, [
        {
          name: "survey_answer",
          props: {
            attacking: answers.attacking,
            defending: answers.defending,
            secondBattle: answers.secondBattle,
          },
          ts: new Date().toISOString(),
        },
      ]).catch(() => undefined);
    }
    storage.set(SURVEY_KEY, "done");
    finish(get, set);
  },
}));

/**
 * Leaving the tutorial, however it ends.
 *
 * Hands the editor back the board it had before the tutorial borrowed it. A
 * player who replays the tutorial from the More tab must not find their village
 * replaced by a practice maze afterwards.
 */
function finish(
  get: () => TutorialState,
  set: (patch: Partial<TutorialState>) => void
): void {
  const saved = get().saved;
  if (saved) useEditorStore.getState().load(saved);
  // The maze they built in T2 is offered as their first real defence. The save
  // store decides once it knows whether the server already holds one — a
  // returning player replaying the tutorial keeps the defence they had.
  const kept = get().kept;
  if (kept) storage.set(TUTORIAL_MAZE_KEY, JSON.stringify(fromLayout(kept)));
  storage.set(DONE_KEY, "done");
  set({ active: false, phase: "done", nudge: null, saved: null });
}

/** Their first real battle is over: the survey may be asked now. */
export function surveyAfterBattle(): void {
  if (storage.get(SURVEY_KEY) === "pending") storage.set(SURVEY_KEY, "ready");
}

/** Open the survey if it is due and nothing else is on top. Returns whether it opened. */
export function openSurveyIfDue(): boolean {
  if (storage.get(SURVEY_KEY) !== "ready") return false;
  const state = useTutorialStore.getState();
  if (state.active) return false;
  useTutorialStore.setState({ active: true, phase: "survey", saved: null, kept: null, nudge: null });
  return true;
}

/** Whether the survey can be sent: the gate needs all three answers. */
export function surveyComplete(answers: SurveyAnswers): boolean {
  return (
    answers.attacking !== null && answers.defending !== null && answers.secondBattle !== null
  );
}

export { TUTORIAL };
export type { TutorialStep };
